export const readAdditionalInfoFetched = (payload: unknown): boolean => {
  if (!payload || typeof payload !== 'object') return false

  const record = payload as Record<string, unknown>
  if (typeof record.is_additional_info_fetched === 'boolean') {
    return record.is_additional_info_fetched
  }

  const nestedData = record.data
  if (nestedData && typeof nestedData === 'object') {
    const nestedRecord = nestedData as Record<string, unknown>
    if (typeof nestedRecord.is_additional_info_fetched === 'boolean') {
      return nestedRecord.is_additional_info_fetched
    }
  }

  return false
}

export const platformNeedsOwnerFieldMapping = (payload: unknown): boolean =>
  !readAdditionalInfoFetched(payload)

export const readOwnerFieldCount = (payload: unknown): number => {
  if (!payload || typeof payload !== 'object') return 0

  const record = payload as Record<string, unknown>
  if (Array.isArray(record.data)) return record.data.length

  const nestedData = record.data
  if (nestedData && typeof nestedData === 'object') {
    const nestedRecord = nestedData as Record<string, unknown>
    if (Array.isArray(nestedRecord.data)) return nestedRecord.data.length
  }

  return 0
}

export const hasOwnerFieldsToConfigure = (ownerFieldsPayload: unknown): boolean =>
  readOwnerFieldCount(ownerFieldsPayload) > 0

type OwnerFieldMappingDetailRow = {
  destination_platform_data_id?: number | string | null
  destination_platform_data?: {
    id?: number
  } | null
}

type OwnerFieldMappingDetail = {
  fieldmappingdata_set?: OwnerFieldMappingDetailRow[]
}

type OwnerFieldSnapshot = {
  id: number
  platform_field_data_set?: Array<{is_processed?: boolean}>
  field_mapping_details?: OwnerFieldMappingDetail | OwnerFieldMappingDetail[] | null
}

const hasDestinationMappingRow = (entry: OwnerFieldMappingDetailRow): boolean =>
  Boolean(entry.destination_platform_data_id) || Boolean(entry.destination_platform_data?.id)

const readOwnerFieldMappingDetails = (field: OwnerFieldSnapshot): OwnerFieldMappingDetail[] => {
  const raw = field.field_mapping_details
  if (raw == null) return []
  if (Array.isArray(raw)) return raw
  if (typeof raw === 'object') return [raw]
  return []
}

type SavedOwnerFieldMapping = {
  destination_field?: {id?: number}
  destination_field_id?: number
  fieldmappingdata_set?: Array<{
    destination_platform_data_id?: number | string | null
    destination_platform_data?: {id?: number} | null
  }>
}

export const readOwnerFields = (payload: unknown): OwnerFieldSnapshot[] => {
  if (!payload || typeof payload !== 'object') return []

  const record = payload as Record<string, unknown>
  if (Array.isArray(record.data)) return record.data as OwnerFieldSnapshot[]

  const nestedData = record.data
  if (nestedData && typeof nestedData === 'object') {
    const nestedRecord = nestedData as Record<string, unknown>
    if (Array.isArray(nestedRecord.data)) return nestedRecord.data as OwnerFieldSnapshot[]
  }

  return []
}

export const readFieldMappingsList = (payload: unknown): SavedOwnerFieldMapping[] => {
  if (Array.isArray(payload)) return payload as SavedOwnerFieldMapping[]
  if (!payload || typeof payload !== 'object') return []

  const record = payload as Record<string, unknown>
  if (Array.isArray(record.data)) return record.data as SavedOwnerFieldMapping[]

  return []
}

export const hasOwnerFieldMappingFromDetails = (field: OwnerFieldSnapshot): boolean =>
  readOwnerFieldMappingDetails(field).some((detail) =>
    detail.fieldmappingdata_set?.some((entry) => hasDestinationMappingRow(entry))
  )

export const hasSavedOwnerFieldMapping = (
  ownerFieldId: number,
  savedMappings: SavedOwnerFieldMapping[]
): boolean => {
  const mapping = savedMappings.find(
    (item) =>
      item.destination_field?.id === ownerFieldId || item.destination_field_id === ownerFieldId
  )

  if (!mapping?.fieldmappingdata_set?.length) return false

  return mapping.fieldmappingdata_set.some(
    (entry) =>
      Boolean(entry.destination_platform_data_id) || Boolean(entry.destination_platform_data?.id)
  )
}

export const areAllOwnerFieldMappingsSavedFromOwnerFields = (
  ownerFieldsPayload: unknown
): boolean => {
  const ownerFields = readOwnerFields(ownerFieldsPayload)
  if (!ownerFields.length) return false

  return ownerFields.every(
    (field) =>
      hasOwnerFieldMappingFromDetails(field) ||
      field.platform_field_data_set?.some((option) => option.is_processed === true)
  )
}

export const areAllOwnerFieldMappingsSaved = (
  ownerFieldsPayload: unknown,
  fieldMappingsPayload: unknown
): boolean => {
  const ownerFields = readOwnerFields(ownerFieldsPayload)
  if (!ownerFields.length) return false

  const savedMappings = readFieldMappingsList(fieldMappingsPayload)
  if (savedMappings.length) {
    return ownerFields.every((field) => hasSavedOwnerFieldMapping(field.id, savedMappings))
  }

  return areAllOwnerFieldMappingsSavedFromOwnerFields(ownerFieldsPayload)
}

type OwnerFieldPlatformSnapshot = {
  isInitComplete?: boolean | null
  is_additional_info_fetched?: boolean | null
}

export const shouldPromptOwnerFieldMapping = (params: {
  statusPayload?: unknown
  ownerFieldsPayload?: unknown
  fieldMappingsPayload?: unknown
  platform?: OwnerFieldPlatformSnapshot | null
}): boolean => {
  const {statusPayload, ownerFieldsPayload, fieldMappingsPayload, platform} = params

  if (!hasOwnerFieldsToConfigure(ownerFieldsPayload)) return false
  if (areAllOwnerFieldMappingsSaved(ownerFieldsPayload, fieldMappingsPayload)) return false
  if (platform?.isInitComplete === false) return true
  if (platform?.is_additional_info_fetched === false) return true

  return platformNeedsOwnerFieldMapping(statusPayload)
}

export const needsPostConnectChannelSettings = (params: {
  connectedPlatform?: OwnerFieldPlatformSnapshot | null
  statusPayload?: unknown
  ownerFieldsPayload?: unknown
}): boolean => {
  const {connectedPlatform, statusPayload, ownerFieldsPayload} = params

  if (connectedPlatform?.isInitComplete === false) return true

  return shouldPromptOwnerFieldMapping({
    statusPayload,
    ownerFieldsPayload,
    platform: connectedPlatform,
  })
}
